import React, { useEffect, useState } from 'react';
import { Avatar, Box, CircularProgress, Dialog, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useDirection } from '../../context/DirectionContext';
import { formatDisplayDate } from '../../utils/dateFormat';
import { translate as tr } from '../../i18n/translations';
import {
  CandidateDetailRecord,
  CandidateStatus,
  CandidateStatusDto,
  FormDefinition,
  FormSubmissionRecord,
} from '../../types/api';
import { T, TIER_RAIL, Tier } from '../../theme/tokens';
import ScoreBox, { ColorVariant } from '../command-center/ScoreBox';
import WeightedChip, { genericWeightsOf } from './WeightedChip';
import { formWeightsOf } from '../../utils/weights';
import GradeBar from '../command-center/GradeBar';
import { findEnrichedValue } from '../../utils/csvExport';
import {
  classifySubmissionAnswers,
  humanLabel,
  isInterviewerRelevantForm,
  FieldMeta,
  FormAnswerEntry,
  NumericAnswerEntry,
} from '../../utils/formAnswerUtils';
import { computeFormScores } from '../../utils/formScoreUtils';

// Free Text card accent for interviewer-relevant generic forms — a faded
// wash of #3E6B93 (a cool blue with no other use in this earthy palette, so
// it reads unambiguously as distinct from tier/danger coloring), following
// the same "local rgba tint" pattern as CandidateListPanel's TIER_BLOCK_TINT.
const INTERVIEW_CARD_BG     = 'rgba(62,107,147,0.08)';
const INTERVIEW_CARD_BORDER = 'rgba(62,107,147,0.25)';

// Reuses ScoreBox's existing per-type color variants for the drawer header's
// live per-form score boxes — an unrecognized/unexpected formType falls back
// to the neutral 'commander' variant (defined in ScoreBox but otherwise
// unused today).
const FORM_TYPE_VARIANT: Record<string, ColorVariant> = {
  generic: 'assessment',
  socio: 'socio',
  interview: 'interview',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusConfig(s: CandidateStatus, lang: Parameters<typeof tr>[1]): { label: string; color: string } {
  switch (s) {
    case CandidateStatus.ACTIVE:             return { label: tr('status.active',    lang), color: T.tierTop };
    case CandidateStatus.INACTIVE:           return { label: tr('status.withdrawn', lang), color: T.danger };
    case CandidateStatus.GIBUSH_COMPLETED:   return { label: tr('status.completed', lang), color: T.ink };
    case CandidateStatus.GIBUSH_INCOMPLETED: return { label: tr('status.incomplete', lang), color: T.tierMid };
    default:                                 return { label: s,                             color: T.textSubtle };
  }
}

function parseSubmissionFields(sub: FormSubmissionRecord): Record<string, string> {
  const data = sub.submissionData;
  const commentsRaw = data.comments;
  const toStrMap = (obj: Record<string, unknown>): Record<string, string> =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '')]));
  if (typeof commentsRaw === 'string' && commentsRaw.trim().startsWith('{')) {
    try { return toStrMap(JSON.parse(commentsRaw) as Record<string, unknown>); } catch { /* fall through */ }
  }
  if (commentsRaw && typeof commentsRaw === 'object') {
    return toStrMap(commentsRaw as Record<string, unknown>);
  }
  const SYSTEM = new Set(['rating', 'position', 'team-size', 'comments']);
  return Object.fromEntries(
    Object.entries(data).filter(([k]) => !SYSTEM.has(k)).map(([k, v]) => [k, String(v ?? '')])
  );
}

function isFlag(value: string, key: string): boolean {
  const likelyFlagKey = /flag|concern|warning|alert/i.test(key);
  return (value === 'true' || likelyFlagKey) && value === 'true';
}

// ── Collapsible section ───────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  count?: number;
  danger?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, count, danger = false, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ border: `1px solid ${T.border}`, mb: '8px' }}>
      <Box
        onClick={() => setOpen(v => !v)}
        sx={{
          px: '14px', py: '9px', cursor: 'pointer', bgcolor: T.surfaceAlt,
          display: 'flex', alignItems: 'center', gap: '8px',
          '&:hover': { bgcolor: '#EDE8DC' },
        }}
      >
        {danger && (
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: T.danger, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.text, flex: 1, fontWeight: 700 }}>
          {title}
        </Typography>
        {count !== undefined && count > 0 && (
          <Box sx={{ px: '5px', py: '1px', bgcolor: danger ? 'rgba(184,80,66,0.12)' : T.borderSoft, borderRadius: '2px' }}>
            <Typography sx={{ fontSize: 10, color: danger ? T.danger : T.textMuted, fontWeight: 500 }}>{count}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 9, color: T.textFaint }}>{open ? '▲' : '▼'}</Typography>
      </Box>
      {open && <Box sx={{ p: '14px', bgcolor: T.surface }}>{children}</Box>}
    </Box>
  );
};

// Object URL for a session-local candidate image File; revoked on change/unmount.
// The created URL is stored alongside the File it was created for, and only
// returned when that stored File still matches the current `file` argument —
// state updates from this effect land after paint, so without this guard the
// drawer would briefly render the *previous* candidate's photo (or that
// candidate's photo when the new one has none) for one frame after switching.
function useObjectUrl(file: File | undefined): string | null {
  const [entry, setEntry] = useState<{ file: File; url: string } | null>(null);
  useEffect(() => {
    if (!file) {
      setEntry(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setEntry({ file, url: u });
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return entry && entry.file === file ? entry.url : null;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CandidateDetailDrawerProps {
  candidate: CandidateStatusDto | null;
  detail: CandidateDetailRecord | null | undefined;
  detailLoading: boolean;
  formDefs: FormDefinition[];
  tier: Tier | null;
  candidateRankInTeam?: string | null;
  rankEvaluatorName?: string | null;
  csvEnrichment?: Record<string, string>;
  /** Session-local image File for this candidate (from the Import Images folder). */
  candidateImage?: File;
  hideSocioEvals?: boolean;
  /** True when rendered inside the fullscreen overlay — trims padding/gaps.
   * The Free Text / Numerical Parameters grids use auto-fit sizing (not a
   * fixed column count), so they already widen on their own with whatever
   * extra space fullscreen frees up. */
  isFullscreen?: boolean;
}

// ── Main component ────────────────────────────────────────────────────────────

const CandidateDetailDrawer: React.FC<CandidateDetailDrawerProps> = ({
  candidate, detail, detailLoading, formDefs, tier, candidateRankInTeam, rankEvaluatorName, csvEnrichment, candidateImage, hideSocioEvals = false, isFullscreen = false,
}) => {
  const { lang } = useDirection();
  const imageUrl = useObjectUrl(candidateImage);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // ── Empty state ──
  if (!candidate) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: T.surface }}>
        <Box sx={{ width: 32, height: 32, border: `1px solid ${T.border}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '12px' }}>
          <Typography sx={{ fontSize: 16, color: T.textFaint }}>↗</Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: T.textFaint }}>{tr('drawer.empty', lang)}</Typography>
      </Box>
    );
  }
  const sc          = statusConfig(candidate.status, lang);
  const formDefMap  = new Map(formDefs.map(fd => [fd.id, fd]));
  // Locally enriched CSV data outranks the backend for display purposes — an
  // admin importing a roster is actively correcting/supplementing what the
  // API knows, so it should win here. Nothing is written back to the API;
  // this only affects what's rendered in this session.
  const csvName = findEnrichedValue(csvEnrichment, ['name', 'candidateName', 'fullName', 'candidateFullName']);
  const displayName =
    csvName
    ?? detail?.fullName
    ?? (detail?.firstName ? `${detail.firstName} ${detail.lastName ?? ''}`.trim() : null)
    ?? candidate.fullName
    ?? (candidate.firstName ? `${candidate.firstName} ${candidate.lastName ?? ''}`.trim() : null)
    ?? `#${candidate.candidateNumber}`;

  const csvMatam = findEnrichedValue(csvEnrichment, ['matam', 'candidateMatam', 'personalMatam', 'candidatePersonalMatam']);
  const personalMatam = csvMatam
    ?? (candidate.candidatePersonalMatam != null ? String(candidate.candidatePersonalMatam) : tr('tier.unknown', lang));

  // Group submissions by form
  const submissionsByForm = new Map<string, FormSubmissionRecord[]>();
  if (detail) {
    for (const sub of detail.formSubmissions) {
      const arr = submissionsByForm.get(sub.formDefinitionId) ?? [];
      arr.push(sub);
      submissionsByForm.set(sub.formDefinitionId, arr);
    }
  }

  const FORM_TYPE_ORDER: Record<string, number> = { generic: 0, interview: 1, socio: 2 };
  const sortedFormEntries = Array.from(submissionsByForm.entries())
    .filter(([id]) => !(hideSocioEvals && formDefMap.get(id)?.formType === 'socio'))
    .sort(([a], [b]) => {
      const orderA = FORM_TYPE_ORDER[formDefMap.get(a)?.formType ?? ''] ?? 3;
      const orderB = FORM_TYPE_ORDER[formDefMap.get(b)?.formType ?? ''] ?? 3;
      return orderA - orderB;
    });

  // Build per-form fieldKey→metadata map — title, and whether the field is a
  // numeric 'gradebox' or a free-text 'textboxblock'/'selectbox' (FormBuilder's
  // field type vocabulary). Drives which of the two flat sections below
  // (Free Text / Numerical Parameters) each submitted answer lands in.
  const formFieldMetaMaps = new Map<string, Map<string, FieldMeta>>();
  for (const fd of formDefs) {
    const metaMap = new Map<string, FieldMeta>();
    const fields: Array<{ type: string; fieldKey: string; title?: string; textFieldKey?: string; maxGrade?: number }> =
      fd.structure?.structure ?? [];
    for (const f of fields) {
      const kind: FieldMeta['kind'] = f.type === 'gradebox' ? 'grade' : 'text';
      if (f.fieldKey) metaMap.set(f.fieldKey, { title: f.title ?? f.fieldKey, kind, maxGrade: f.maxGrade });
      // A gradebox's own optional free-text note (showTextInput/textFieldKey)
      // is always text, regardless of its parent field's kind.
      if (f.textFieldKey) metaMap.set(f.textFieldKey, { title: f.title ?? f.textFieldKey, kind: 'text' });
    }
    formFieldMetaMaps.set(fd.id, metaMap);
  }

  // Build flagblock label + associated-text map from form structure
  const formFlagDefs = new Map<string, Map<string, { label: string; textFieldKey?: string }>>();
  for (const fd of formDefs) {
    const structFields: Array<{ type: string; fieldKey: string; title?: string; textFieldKey?: string }> =
      fd.structure?.structure ?? [];
    const defMap = new Map<string, { label: string; textFieldKey?: string }>();
    for (const f of structFields) {
      if (f.type === 'flagblock') defMap.set(f.fieldKey, { label: f.title ?? f.fieldKey, textFieldKey: f.textFieldKey });
    }
    formFlagDefs.set(fd.id, defMap);
  }

  // Aggregate flags — structure-aware, with associated text content
  const allFlags: Array<{ formName: string; evaluator: string; label: string; text: string | null }> = [];
  if (detail) {
    for (const sub of detail.formSubmissions) {
      const fd = formDefMap.get(sub.formDefinitionId);
      const formName = fd?.name ?? 'Unknown Form';
      const evaluator = `${sub.submittedBy.firstName} ${sub.submittedBy.lastName}`.trim();
      const fields = parseSubmissionFields(sub);
      const flagDefMap = formFlagDefs.get(sub.formDefinitionId) ?? new Map();

      for (const [key, value] of Object.entries(fields)) {
        if (value !== 'true') continue;
        const def = flagDefMap.get(key);
        // Accept if defined in structure as flagblock, OR matches heuristic
        if (!def && !isFlag(value, key)) continue;
        const label = def?.label ?? humanLabel(key);
        const text  = def?.textFieldKey ? (fields[def.textFieldKey] || null) : null;
        allFlags.push({ formName, evaluator, label, text });
      }
    }
  }

  // Flag-associated note fields (a flagblock's own textFieldKey) are already
  // surfaced under Concerns above — excluded here so they don't also show up
  // as a duplicate generic text entry.
  const flagNoteKeysByForm = new Map<string, Set<string>>();
  formFlagDefs.forEach((defMap, formId) => {
    const keys = new Set<string>();
    defMap.forEach(def => { if (def.textFieldKey) keys.add(def.textFieldKey); });
    flagNoteKeysByForm.set(formId, keys);
  });

  // Flatten every submitted answer — across every form and evaluator — into
  // two lists by kind, each entry tagged with who answered it and from which
  // form. Replaces the old per-form/per-submission card layout: grouping by
  // form meant a reviewer had to open every card to find, say, all the free-
  // text write-ups, and capped the grid at one column per *form* regardless
  // of how much screen width was actually available. Classification itself
  // (classifySubmissionAnswers) is tested in formAnswerUtils.test.ts.
  const allTextEntries: FormAnswerEntry[] = [];
  const allNumericEntries: NumericAnswerEntry[] = [];

  for (const [formDefId, subs] of sortedFormEntries) {
    const fd = formDefMap.get(formDefId);
    const formName = fd?.name ?? formDefId;
    const metaMap = formFieldMetaMaps.get(formDefId) ?? new Map<string, FieldMeta>();
    const flagNoteKeys = flagNoteKeysByForm.get(formDefId) ?? new Set<string>();

    // Socio submissions carry no real free text — their `comments` is just
    // the computed `{ socioScore }`, which has no form-structure meta (socio
    // forms use `structure.boxes`, not `structure.structure`) and would
    // otherwise fall through to the text bucket and show up as a bogus
    // "Socio Score" card. The score itself is already surfaced via the
    // header ScoreBox and Score Breakdown, so just drop the text side here;
    // numeric entries (the per-submission rating) are left untouched.
    const isSocio = fd?.formType === 'socio';
    const isInterviewerForm = isInterviewerRelevantForm(fd?.formType, fd?.roleRelevancy);

    for (const sub of subs) {
      const evaluator = `${sub.submittedBy.firstName} ${sub.submittedBy.lastName}`.trim() || tr('filter.evaluator', lang);
      const rating = sub.submissionData.rating != null ? Number(sub.submissionData.rating) : null;
      const { text, numeric } = classifySubmissionAnswers(
        parseSubmissionFields(sub), rating, formName, evaluator, tr('drawer.rating', lang), metaMap, flagNoteKeys
      );
      if (!isSocio) allTextEntries.push(...text.map(t => ({ ...t, isInterviewerForm })));
      allNumericEntries.push(...numeric);
    }
  }

  // Score weights from form defs
  const assessWeights = genericWeightsOf(formDefs);
  const socioWeight     = formDefs.filter(fd => fd.formType === 'socio').reduce((s, fd) => s + fd.weightInFinalScore, 0);
  const assessWeight    = formDefs.filter(fd => fd.formType === 'generic').reduce((s, fd) => s + fd.weightInFinalScore, 0);
  const interviewWeight = formDefs.filter(fd => fd.formType === 'interview').reduce((s, fd) => s + fd.weightInFinalScore, 0);

  const hasAnyScore = candidate.finalCycleScore != null || candidate.finalSocioScore != null ||
    candidate.finalAssesmentScore != null || candidate.finalInterviewScore != null;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: T.surface }}>

      {/* ── Header card ── */}
      <Box sx={{ px: isFullscreen ? '16px' : '20px', py: isFullscreen ? '10px' : '16px', borderBottom: `1px solid ${T.border}`, bgcolor: T.surfaceAlt, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: '14px' }}>
          {/* Candidate photo — session-local image when imported, placeholder otherwise */}
          <Avatar
            variant="square"
            src={imageUrl ?? undefined}
            onClick={imageUrl ? () => setLightboxOpen(true) : undefined}
            sx={{
              width: 96,
              minHeight: 96,
              borderRadius: '4px',
              border: `1px solid ${T.border}`,
              bgcolor: T.borderSoft,
              flexShrink: 0,
              cursor: imageUrl ? 'pointer' : 'default',
              '& img': { objectFit: 'cover' },
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 40, color: T.textFaint }} />
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name + badges */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '6px' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.2, flex: 1, mr: '8px' }}>
                {displayName}
              </Typography>
              {tier && (
                <Box sx={{ px: '8px', py: '2px', border: `1px solid ${TIER_RAIL[tier]}`, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 10, color: TIER_RAIL[tier], fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {tr(`tier.${tier}`, lang)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Identifiers */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '10px', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 11, color: T.textMuted, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                {tr('drawer.candidateNumber', lang)} {candidate.candidateNumber}
              </Typography>
              <Typography sx={{ fontSize: 11, color: T.textFaint }}>·</Typography>
              <Typography sx={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>{tr('filter.team', lang)} {candidate.teamNumber}</Typography>
              <Typography sx={{ fontSize: 11, color: T.textFaint }}>·</Typography>
              <Typography sx={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>
                {tr('drawer.matam', lang)}: {personalMatam}
              </Typography>
              {candidateRankInTeam != null && (
                <>
                  <Typography sx={{ fontSize: 11, color: T.textFaint }}>·</Typography>
                  <Typography sx={{ fontSize: 11, color: T.textMuted, fontWeight: 500 }}>
                    {candidateRankInTeam === 'unknown'
                      ? tr('drawer.rankUnknown', lang)
                      : `${tr('drawer.rankLabel', lang)} ${candidateRankInTeam}`}
                  </Typography>
                  {rankEvaluatorName && (
                    <>
                      <Typography sx={{ fontSize: 11, color: T.textFaint }}>·</Typography>
                      <Typography sx={{ fontSize: 11, color: T.textFaint }}>
                        {tr('drawer.rankedBy', lang)}: {rankEvaluatorName}
                      </Typography>
                    </>
                  )}
                </>
              )}
              {/* Status badge */}
              <Box sx={{ marginInlineStart: '2px', px: '6px', py: '2px', border: `1px solid ${sc.color}`, borderRadius: '2px' }}>
                <Typography sx={{ fontSize: 10, color: sc.color, fontWeight: 500 }}>{sc.label}</Typography>
              </Box>
              {candidate.redFlag && (
                <Box sx={{ px: '6px', py: '2px', border: `1px solid ${T.danger}`, bgcolor: 'rgba(184,80,66,0.06)', borderRadius: '2px' }}>
                  <Typography sx={{ fontSize: 10, color: T.danger, fontWeight: 500 }}>⚑ {tr('drawer.redFlag', lang)}</Typography>
                </Box>
              )}
              {!detailLoading && detail && allFlags.length > 0 && (
                <Box sx={{ px: '6px', py: '2px', border: `1px solid ${T.danger}`, bgcolor: 'rgba(184,80,66,0.10)', borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Typography sx={{ fontSize: 10, color: T.danger, fontWeight: 700 }}>⚑</Typography>
                  <Typography sx={{ fontSize: 10, color: T.danger, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{allFlags.length}</Typography>
                </Box>
              )}
            </Box>

            {/* Score boxes row — Final stays sourced from the (reliable)
                finalCycleScore field; the rest are computed live from this
                candidate's raw form submissions rather than the cycle-status
                endpoint's finalSocioScore/finalAssesmentScore/
                finalInterviewScore fields, which have been observed going
                stale. See docs/superpowers/specs/2026-08-26-admin-per-form-scores-design.md. */}
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <ScoreBox label={tr('score.final', lang)} value={candidate.finalCycleScore} variant="final" />
              {detail && computeFormScores(detail.formSubmissions, formDefMap).map(entry => (
                <ScoreBox
                  key={entry.formDefinitionId}
                  label={entry.name}
                  value={entry.score}
                  variant={FORM_TYPE_VARIANT[entry.formType] ?? 'commander'}
                  badge={(() => {
                    const fd = formDefMap.get(entry.formDefinitionId);
                    const w = fd?.formType === 'generic' ? formWeightsOf(fd.structure?.structure) : null;
                    return w ? <WeightedChip weights={w} /> : undefined;
                  })()}
                />
              ))}
              {detailLoading && !detail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '8px' }}>
                  <CircularProgress size={12} sx={{ color: T.textFaint }} />
                  <Typography sx={{ fontSize: 10, color: T.textFaint }}>{tr('loading', lang)}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: isFullscreen ? '18px' : '14px', py: isFullscreen ? '10px' : '12px', display: 'flex', flexDirection: 'column', gap: isFullscreen ? '8px' : '10px' }}>

        {/* Locally enriched (CSV import) data bar — arbitrary fields only, not names/predefined */}
        {csvEnrichment && Object.keys(csvEnrichment).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', p: '8px', border: `1px solid ${T.borderSoft}`, bgcolor: T.surfaceAlt }}>
            {Object.entries(csvEnrichment).map(([key, value]) => (
              <Box key={key} sx={{ px: '7px', py: '3px', border: `1px solid ${T.border}`, bgcolor: T.surface, display: 'flex', gap: '4px', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{humanLabel(key)}:</Typography>
                <Typography sx={{ fontSize: 11, color: T.text }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Concerns (red flags) — surfaced first: right under the header/banner,
             before any form content, so a reviewer sees risk signals immediately ── */}
        <Section
          title={tr('drawer.concerns', lang)}
          count={allFlags.length}
          danger={allFlags.length > 0}
          defaultOpen={allFlags.length > 0}
        >
          {!detail && detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: '8px' }}><CircularProgress size={14} sx={{ color: T.textFaint }} /></Box>
          ) : allFlags.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: T.textFaint, fontStyle: 'italic' }}>{tr('drawer.noConcerns', lang)}</Typography>
          ) : (
            allFlags.map((f, i) => (
              <Box key={i} sx={{ p: '10px', mb: i < allFlags.length - 1 ? '6px' : 0, bgcolor: 'rgba(184,80,66,0.04)', border: `1px solid rgba(184,80,66,0.2)` }}>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <Box sx={{ width: 5, height: 5, bgcolor: T.danger, borderRadius: '50%', flexShrink: 0, mt: '4px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.danger }}>{f.label}</Typography>
                    <Typography sx={{ fontSize: 10, color: T.textFaint, mt: '2px' }}>{f.evaluator} · {f.formName}</Typography>
                  </Box>
                </Box>
                {f.text && (
                  <Box sx={{ mt: '8px', marginInlineStart: '13px', bgcolor: T.surface, border: `1px solid rgba(184,80,66,0.15)`, px: '8px', py: '6px' }}>
                    <Typography sx={{ fontSize: 12, color: T.text, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{f.text}</Typography>
                  </Box>
                )}
              </Box>
            ))
          )}
        </Section>

        {/* ── Free Text — every write-up across every form/evaluator, flattened
             and attributed, so nothing is hidden inside a per-form card ── */}
        {(sortedFormEntries.length > 0 || (detailLoading && !detail)) && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textMuted, fontWeight: 700 }}>
                {tr('drawer.freeText', lang)}
              </Typography>
              {detail && allTextEntries.length > 0 && (
                <Box sx={{ px: '5px', py: '1px', bgcolor: T.borderSoft }}>
                  <Typography sx={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{allTextEntries.length}</Typography>
                </Box>
              )}
              {detailLoading && !detail && (
                <CircularProgress size={11} sx={{ color: T.textFaint }} />
              )}
            </Box>

            {detail && allTextEntries.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: T.textFaint, fontStyle: 'italic' }}>{tr('drawer.noFreeText', lang)}</Typography>
            ) : (
              // auto-fit (not auto-fill) so the existing entries actually
              // stretch to fill available width — auto-fill would keep
              // reserving empty tracks past however many entries exist,
              // leaving blank space instead of widening the real ones.
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                {allTextEntries.map((entry, i) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: entry.isInterviewerForm ? INTERVIEW_CARD_BG : T.surfaceAlt,
                      border: `1px solid ${entry.isInterviewerForm ? INTERVIEW_CARD_BORDER : T.borderSoft}`,
                      px: '10px',
                      py: '8px',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', mb: '4px' }}>
                      <Typography sx={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                        {entry.label}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: T.textFaint, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {entry.evaluator} · {entry.formName}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: T.text, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {entry.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Numerical Parameters — same flattening, for gradebox answers and
             each submission's overall rating ── */}
        {(sortedFormEntries.length > 0 || (detailLoading && !detail)) && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textMuted, fontWeight: 700 }}>
                {tr('drawer.numericalParams', lang)}
              </Typography>
              {detail && allNumericEntries.length > 0 && (
                <Box sx={{ px: '5px', py: '1px', bgcolor: T.borderSoft }}>
                  <Typography sx={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{allNumericEntries.length}</Typography>
                </Box>
              )}
              {detailLoading && !detail && (
                <CircularProgress size={11} sx={{ color: T.textFaint }} />
              )}
            </Box>

            {detail && allNumericEntries.length === 0 ? (
              <Typography sx={{ fontSize: 11, color: T.textFaint, fontStyle: 'italic' }}>{tr('drawer.noNumericalParams', lang)}</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                {allNumericEntries.map((entry, i) => (
                  <Box key={i} sx={{ border: `1px solid ${T.border}`, px: '10px', py: '8px' }}>
                    <GradeBar label={entry.label} value={entry.value} max={entry.max ?? undefined} />
                    <Typography sx={{ fontSize: 10, color: T.textFaint, mt: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.evaluator} · {entry.formName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── Row: Score Breakdown + Details — lower priority now: the final
             score is already visible in the header, so the weighted formula
             is supplementary context, not something to lead with ── */}
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>

          {/* Score Breakdown */}
          {hasAnyScore && (
            <Box sx={{ flex: 1.4, minWidth: 260, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {/* Formula card */}
              <Box sx={{ flex: 1, minWidth: 200, border: `1px solid ${T.border}`, p: '12px' }}>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.textMuted, mb: '8px', fontWeight: 700 }}>
                  {tr('drawer.scoreBreakdown', lang)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { label: tr('score.socio',       lang), weight: socioWeight,     value: candidate.finalSocioScore, weighted: null as ReturnType<typeof genericWeightsOf> },
                    { label: tr('score.assessment',  lang), weight: assessWeight,    value: candidate.finalAssesmentScore, weighted: assessWeights },
                    { label: tr('score.interview',   lang), weight: interviewWeight, value: candidate.finalInterviewScore, weighted: null as ReturnType<typeof genericWeightsOf> },
                  ].filter(item => item.weight > 0).map((item, i, arr) => (
                    <React.Fragment key={item.label}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                          {item.label} ×{Math.round(item.weight * 100)}%
                        </Typography>
                        {item.weighted && <WeightedChip weights={item.weighted} />}
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: item.value != null ? T.ink : T.textFaint, fontVariantNumeric: 'tabular-nums' }}>
                          {item.value != null ? Number(item.value).toFixed(1) : '—'}
                        </Typography>
                      </Box>
                      {i < arr.length - 1 && (
                        <Typography sx={{ fontSize: 11, color: T.textFaint, mx: '2px' }}>+</Typography>
                      )}
                    </React.Fragment>
                  ))}
                  {[socioWeight, assessWeight, interviewWeight].some(w => w > 0) && (
                    <>
                      <Typography sx={{ fontSize: 11, color: T.textFaint, mx: '2px' }}>=</Typography>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{tr('score.final', lang)}</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
                          {candidate.finalCycleScore != null ? Number(candidate.finalCycleScore).toFixed(1) : '—'}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {candidate.finalCycleScore == null && candidate.finalSocioScore == null &&
                   candidate.finalAssesmentScore == null && candidate.finalInterviewScore == null && (
                    <Box sx={{ px: '6px', py: '2px', border: `1px solid ${T.borderInput}` }}>
                      <Typography sx={{ fontSize: 10, color: T.textSubtle }}>{tr('drawer.partial', lang)}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Alert card — only when red flag */}
              {candidate.redFlag && (
                <Box sx={{
                  border: `1px solid rgba(184,80,66,0.35)`,
                  bgcolor: 'rgba(184,80,66,0.04)',
                  p: '12px',
                  minWidth: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                }}>
                  <Typography sx={{ fontSize: 10, color: T.danger, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{tr('drawer.redFlag', lang)}</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 500, color: T.danger, lineHeight: 1 }}>1</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Details */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Section title={tr('drawer.details', lang)} defaultOpen={false}>
              {!candidate.inactivationReason && !candidate.lastStatusChangeAt ? (
                <Typography sx={{ fontSize: 11, color: T.textFaint, fontStyle: 'italic' }}>{tr('drawer.noDetails', lang)}</Typography>
              ) : (
                <>
                  {candidate.inactivationReason && (
                    <Box sx={{ mb: '10px' }}>
                      <Typography sx={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: '4px', fontWeight: 600 }}>{tr('drawer.inactivationReason', lang)}</Typography>
                      <Typography sx={{ fontSize: 12, color: T.text }}>{candidate.inactivationReason}</Typography>
                    </Box>
                  )}
                  {candidate.inactivationReasonText && (
                    <Box sx={{ mb: '10px' }}>
                      <Typography sx={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: '4px', fontWeight: 600 }}>{tr('drawer.note', lang)}</Typography>
                      <Typography sx={{ fontSize: 12, color: T.text, lineHeight: 1.5 }}>{candidate.inactivationReasonText}</Typography>
                    </Box>
                  )}
                  {candidate.lastStatusChangeAt && (
                    <Box>
                      <Typography sx={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: '4px', fontWeight: 600 }}>{tr('drawer.lastStatus', lang)}</Typography>
                      <Typography sx={{ fontSize: 12, color: T.text }}>{formatDisplayDate(candidate.lastStatusChangeAt, lang)}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Section>
          </Box>

        </Box>

      </Box>

      {/* ── Image lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: 0 } } }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt=""
            onClick={() => setLightboxOpen(false)}
            sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default CandidateDetailDrawer;
